import { state, api, $, $$, toast, escHtml, truncate } from './core';
import { _aiMode } from './auth-ui';
import type { ChatMedia, ChatQueryResult, ChatSource } from './types';
import type { RandChatHistory, RandMachines } from './tipuri-baza';

/** /api/chat (routes/chat.py) — câmpurile citite de interfață */
interface RaspunsChat {
    response?: string; error?: string; source?: string; confidence?: number | null; detected_machine?: string | null;
    sources?: ChatSource[]; query_results?: ChatQueryResult[]; media?: ChatMedia | null;
    cloud_budget?: Parameters<typeof updateCloudBudgetBadge>[0]; clarificare?: Clarificare;
}

function _getChatLineId(): string {
    try {
        return String((window as unknown as { __chatLineId?: string }).__chatLineId || '').trim();
    } catch (_) {
        return '';
    }
}

/* ═══ AI CHAT ══════════════════════════════════════════════════ */
export let chatInited = false;

export function ensureChatApiSetup() {
    const wrap = document.getElementById('chat-api-setup');
    if (!wrap || document.getElementById('chat-api-provider')) return;
    wrap.innerHTML = [
        '<p>Selectați furnizorul și introduceți cheia API pentru a activa asistentul AI:</p>',
        '<div class="form-row">',
        '<label for="chat-api-provider">Furnizor</label>',
        '<select id="chat-api-provider" aria-label="Furnizor AI">',
        '<option value="openai">OpenAI (GPT)</option>',
        '<option value="claude">Claude (Anthropic)</option>',
        '</select>',
        '</div>',
        '<div class="form-row">',
        '<label for="chat-api-key">Cheie API</label>',
        '<input type="password" id="chat-api-key" placeholder="sk-... sau cheie Claude" aria-label="Cheie API">',
        '<button type="button" class="btn btn-primary" id="chat-save-key">Salvează</button>',
        '<button type="button" class="btn btn-sm" id="chat-test-key" title="Verifică dacă cheia este validă">Testează cheia</button>',
        '</div>',
        '<p class="chat-api-hint">Cheia este salvată în config și folosită și la procesarea documentelor (Procesează PDF).</p>'
    ].join('');
}
export function ensureChatApiStatusLine() {
    let statusLine = document.getElementById('chat-api-status-line');
    const setupEl = document.getElementById('chat-api-setup');
    if (!statusLine && setupEl && setupEl.parentNode) {
        statusLine = document.createElement('p');
        statusLine.id = 'chat-api-status-line';
        statusLine.className = 'chat-api-status-line';
        statusLine.setAttribute('role', 'status');
        setupEl.parentNode.insertBefore(statusLine, setupEl);
    }
    return statusLine;
}

/** Contor buget AI Cloud (limită lunară $30 — 2026-07-07). */
export function updateCloudBudgetBadge(b: { spent_usd: number; limit_usd: number; pct: number; exceeded: boolean; warning: boolean }) {
    const badge = document.getElementById('cloud-budget-badge');
    if (!badge || !b) return;
    badge.classList.remove('hidden');
    badge.textContent = `☁️ ${b.spent_usd.toFixed(2)}$ / ${b.limit_usd.toFixed(0)}$`;
    badge.title = `Consum AI Cloud luna aceasta: ${b.spent_usd.toFixed(2)}$ din ${b.limit_usd.toFixed(0)}$ (${b.pct}%)`;
    badge.style.color = b.exceeded ? 'var(--red, #e05252)' : (b.warning ? 'var(--orange, #e0a030)' : 'var(--text-muted, #888)');
}

async function refreshCloudBudgetBadge() {
    try {
        const b = await api('/api/chat/cloud_usage');
        if (b && typeof b.spent_usd === 'number') updateCloudBudgetBadge(b);
    } catch { /* endpoint indisponibil — badge rămâne ascuns */ }
}

/* Înălțimea containerului de chat era `calc(100vh - 70px)` — un număr fix pentru
   ceva care nu e fix. Deasupra lui stau bara de sus (care își schimbă înălțimea
   când titlul se rupe pe două rânduri) și padding-ul secțiunii; pe telefon ieșeau
   ~83px, nu 70, iar containerul se termina cu 13px sub marginea vizibilă — adică
   exact peste câmpul de scris și butonul „Trimite", ultimele din coloană.
   Îl măsurăm de la poziția lui reală până la baza ecranului VIZIBIL
   (`visualViewport`, nu `innerHeight`: pe Android al doilea include zona de sub
   bara browserului — lecția din 8 august). CSS-ul păstrează calc-ul ca plasă
   până rulează asta. */
function _potrivesteInaltimeaChatului(): void {
    const c = document.querySelector('.chat-container') as HTMLElement | null;
    if (!c || !c.offsetParent) return;
    const vizibil = window.visualViewport?.height || document.documentElement.clientHeight;
    const sus = c.getBoundingClientRect().top;
    const h = Math.max(320, Math.round(vizibil - sus - 8));
    c.style.height = h + 'px';
    c.style.maxHeight = h + 'px';
}

export async function initChat() {
    /* Măsurarea se face la FIECARE intrare în secțiune, nu o dată: prima
       chemare poate veni cât timp secțiunea e încă ascunsă (înălțime zero), iar
       între vizite se schimbă orientarea ecranului sau înălțimea barei de sus. */
    _potrivesteInaltimeaChatului();
    requestAnimationFrame(_potrivesteInaltimeaChatului);
    void populeazaSelectorulDeMasiniChat();   // după autentificare, nu la încărcarea bundle-ului
    if (chatInited) return;
    chatInited = true;

    // Bara browserului apare/dispare la derulare, tastatura urcă — remăsurăm.
    window.addEventListener('resize', _potrivesteInaltimeaChatului);
    window.visualViewport?.addEventListener('resize', _potrivesteInaltimeaChatului);

    ensureChatApiSetup();
    refreshCloudBudgetBadge();
    const status = await api('/api/chat/status').catch(() => ({}));
    const providerEl = $('#chat-api-provider');
    if (providerEl && status.provider) (providerEl as HTMLInputElement).value = status.provider === 'claude' ? 'claude' : 'openai';
    const statusLine = ensureChatApiStatusLine();
    if (statusLine) {
        if (status.configured) {
            const prov = (status.provider === 'claude') ? 'Claude' : 'OpenAI';
            statusLine.textContent = `Cheie API: configurată (${prov}). Aceeași cheie pentru Chat și pentru Procesează/Reprocesează PDF. Dacă Chat merge dar Procesează dă eroare, cauza e altă (limită API, document blocat).`;
            statusLine.classList.remove('chat-api-status-missing');
        } else {
            statusLine.textContent = 'Cheie API: neconfigurată. Introdu cheia și apasă Salvează (doar master).';
            statusLine.classList.add('chat-api-status-missing');
        }
    }

    const settingsBtn = document.getElementById('chat-settings-btn');
    const isOperator = state.currentUser.role === 'operator';
    if (settingsBtn && isOperator) {
        settingsBtn.style.display = 'none';
    }
    if (statusLine && isOperator) {
        statusLine.style.display = 'none';
    }
    /* 4 sep 2026 (audit): operatorul vedea «Auto / Local / Cloud», bifa
       «Agent» și «☁️ 16,15 $ / 100 $» — butoane de dezvoltator pe ecranul
       mecanicului, și 0 întrebări tastate în 7 zile. Pentru operator rămân
       întrebarea, mașina și istoricul; ruta AI o alege aplicația. */
    if (isOperator) {
        ['ai-mode-selector', 'chat-agentic-toggle', 'cloud-budget-badge'].forEach(id => {
            const el = document.getElementById(id);
            if (el) el.style.display = 'none';
        });
    }
    const setupPanel = document.getElementById('chat-api-setup');
    if (setupPanel && isOperator) {
        setupPanel.style.display = 'none';
    }

    if (!status.configured && state.currentUser.role === 'master') {
        $('#chat-api-setup')!.classList.remove('hidden');
    }

    /* Butonul se ascunde pentru `operator`, dar ascultătorul se punea doar pentru
       `master` — deci inginerul vedea butonul, îl apăsa și nu se întâmpla nimic.
       Un buton vizibil ori face ceva, ori nu se arată: îl legăm pentru toți cei
       care îl văd. Scrierea cheii rămâne a masterului, iar butonul de salvare o
       spune el însuși mai jos, în loc să tacă. */
    if (settingsBtn && !isOperator) {
        settingsBtn.addEventListener('click', () => {
            const panou = $('#chat-api-setup');
            panou!.classList.toggle('hidden');
            // Panoul deschis ia din spațiul de deasupra: fără remăsurare, bara de
            // scris ar fi împinsă sub marginea ecranului.
            _potrivesteInaltimeaChatului();
            /* 19 sep 2026: pe telefon antetul are opt comenzi și derulare proprie
               (`max-height:26dvh`), iar „Setări API" stă pe ultimul rând. După
               apăsare, panoul se deschidea SUB partea vizibilă a antetului: omul
               vedea că nu se întâmplă nimic și mai apăsa o dată, adică îl închidea
               la loc. Îl aducem în ochi. */
            if (!panou!.classList.contains('hidden')) {
                panou!.scrollIntoView({ block: 'nearest' });
            }
        });
    }
    const saveBtn = document.getElementById('chat-save-key') as HTMLButtonElement | null;
    if (saveBtn && !isOperator && state.currentUser.role !== 'master') {
        saveBtn.disabled = true;
        saveBtn.title = 'Cheia API se schimbă doar din contul de master';
        saveBtn.textContent = 'Salvează (doar master)';
    }

    if (state.currentUser.role === 'master') {
        $('#chat-save-key')!.addEventListener('click', async () => {
            const key = ($('#chat-api-key') as HTMLInputElement).value.trim();
            const provider = (providerEl && (providerEl as HTMLInputElement).value) || 'openai';
            if (!key) { toast('Introduceți o cheie API', 'error'); return; }
            const res = await fetch('/api/chat/set_key', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ key, provider })
            });
            const data = await res.json();
            if (res.ok) {
                toast('Cheie API salvată cu succes!');
                $('#chat-api-setup')!.classList.add('hidden');
            } else {
                toast(data.error || 'Eroare la salvare', 'error');
            }
        });
    }

    const testKeyBtn = document.getElementById('chat-test-key');
    if (testKeyBtn && state.currentUser.role === 'master') {
        testKeyBtn.addEventListener('click', async () => {
            const key = ($('#chat-api-key') as HTMLInputElement).value.trim();
            const provider = (providerEl && (providerEl as HTMLInputElement).value) || 'openai';
            if (!key) { toast('Introduceți cheia API în câmp, apoi apăsați Testează.', 'error'); return; }
            (testKeyBtn as HTMLInputElement).disabled = true;
            testKeyBtn.textContent = 'Se verifică…';
            try {
                const res = await fetch('/api/chat/test_key', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ key, provider })
                });
                const data = await res.json();
                if (data.ok) {
                    toast(data.message || 'Cheia este validă.');
                } else {
                    toast(data.error || 'Cheie invalidă.', 'error');
                }
            } catch (e) {
                toast('Eroare la verificare: ' + ((e as Error).message || 'necunoscută'), 'error');
            }
            (testKeyBtn as HTMLInputElement).disabled = false;
            testKeyBtn.textContent = 'Testează cheia';
        });
    }

    if (state.currentUser.role === 'master') {
        fetch('/api/ai_local/config').then(r => r.json()).then(cfg => {
            if (cfg.top_k) (document.getElementById('ai-cfg-topk') as HTMLInputElement).value = cfg.top_k;
            if (cfg.min_score) (document.getElementById('ai-cfg-minscore') as HTMLInputElement).value = cfg.min_score;
            if (cfg.max_tokens) (document.getElementById('ai-cfg-tokens') as HTMLInputElement).value = cfg.max_tokens;
        }).catch(() => {});

        document.getElementById('ai-cfg-save')?.addEventListener('click', async () => {
            const status = document.getElementById('ai-cfg-status');
            const payload = {
                top_k: parseInt((document.getElementById('ai-cfg-topk') as HTMLInputElement).value),
                min_score: parseFloat((document.getElementById('ai-cfg-minscore') as HTMLInputElement).value),
                max_tokens: parseInt((document.getElementById('ai-cfg-tokens') as HTMLInputElement).value),
            };
            try {
                const res = await fetch('/api/ai_local/config', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify(payload)
                });
                const data = await res.json();
                if (data.ok) {
                    status!.textContent = 'Salvat!';
                    status!.style.color = 'var(--green)';
                    toast('Parametri AI actualizați');
                } else {
                    status!.textContent = data.error || 'Eroare';
                    status!.style.color = 'var(--red)';
                }
            } catch(e) { status!.textContent = 'Eroare conexiune'; status!.style.color = 'var(--red)'; }
            setTimeout(() => { status!.textContent = ''; }, 3000);
        });

        document.getElementById('ai-cfg-reindex')?.addEventListener('click', async () => {
            const status = document.getElementById('ai-cfg-status');
            status!.textContent = 'Reindexare în curs...';
            status!.style.color = 'var(--orange)';
            try {
                const res = await fetch('/api/ai_local/reindex', {
                    method: 'POST', headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({mode: 'full'})
                });
                const data = await res.json();
                if (data.ok) {
                    status!.textContent = `Reindexat: ${JSON.stringify(data.stats)}`;
                    status!.style.color = 'var(--green)';
                    toast('RAG reindexat cu succes');
                }
            } catch(e) { status!.textContent = 'Eroare reindex'; status!.style.color = 'var(--red)'; }
        });
    }

    $$('.suggestion-chip').forEach(chip => {
        chip.addEventListener('click', () => {
            ($('#chat-input') as HTMLInputElement).value = chip.dataset.q as string;
            sendChatMessage();
        });
    });

    const chatMachineForSuggestions = document.getElementById('chat-machine');
    if (chatMachineForSuggestions) {
        chatMachineForSuggestions.addEventListener('change', () => {
            try {
                const w = window as unknown as { __chatLineSuppressClear?: boolean; __chatLineId?: string };
                if (!w.__chatLineSuppressClear) {
                    delete w.__chatLineId;
                }
            } catch (_) { /* ignore */ }
            _updateSmartSuggestions((chatMachineForSuggestions as HTMLInputElement).value);
        });
    }

    // History panel
    document.getElementById('chat-history-btn')?.addEventListener('click', () => {
        const panel = document.getElementById('chat-history-panel');
        if (panel) {
            panel.classList.toggle('hidden');
            if (!panel.classList.contains('hidden')) _loadChatHistory();
        }
    });
    document.getElementById('ch-close-btn')?.addEventListener('click', () => {
        document.getElementById('chat-history-panel')?.classList.add('hidden');
    });
    document.getElementById('ch-load-btn')?.addEventListener('click', () => { _chPage = 1; _loadChatHistory(); });
    document.getElementById('ch-search')?.addEventListener('keydown', e => {
        if (e.key === 'Enter') { _chPage = 1; _loadChatHistory(); }
    });
}

$('#chat-form')!.addEventListener('submit', e => {
    e.preventDefault();
    sendChatMessage();
});

/* Umple selectorul de mașină al chatului.
 *
 * Era un IIFE care rula la EVALUAREA bundle-ului și sărea peste fetch dacă
 * `#app-wrapper` avea clasa `auth-locked`. Clasa e scrisă direct în șablon și se
 * scoate abia la `_hideAuthGate()`, adică DUPĂ evaluarea sincronă a bundle-ului —
 * deci condiția era mereu adevărată și lista nu se încărca NICIODATĂ. Selectorul
 * rămânea cu o singură opțiune („Toate mașinile"), pentru toți utilizatorii.
 * Grija din spatele condiției era reală (401-uri zgomotoase imediat după
 * delogare), doar momentul era greșit: acum se cheamă când se deschide chatul,
 * deci după autentificare. */
export async function populeazaSelectorulDeMasiniChat(): Promise<void> {
    const sel = document.getElementById('chat-machine') as HTMLSelectElement | null;
    if (!sel) return;
    if (sel.dataset.populat === '1') return;          // o singură dată
    if ((window.location.search || '').indexOf('logout=1') !== -1) return;
    try {
        const r = await fetch('/api/machines/all', { credentials: 'same-origin' });
        if (!r.ok) return;
        const machines = await r.json();
        if (!machines) return;
        (machines || []).forEach((m: Pick<RandMachines, 'id' | 'name'>) => {
            const opt = document.createElement('option');
            opt.value = m.id;
            opt.textContent = `${m.id} — ${(m.name || '').substring(0, 30)}`;
            sel.appendChild(opt);
        });
        sel.dataset.populat = '1';
    } catch (_) { /* rețeaua căzută: selectorul rămâne cu „Toate mașinile" */ }
}

(function _initChatMachineSelector() {
    const sel = document.getElementById('chat-machine');
    if (!sel) return;
    sel.addEventListener('change', () => {
        const indicator = document.getElementById('chat-context-indicator');
        if ((sel as HTMLInputElement).value && indicator) {
            indicator.textContent = `📡 Context: ${(sel as HTMLInputElement).value}`;
            indicator.style.display = '';
        } else if (indicator) {
            indicator.style.display = 'none';
        }
    });
})();

export function _updateSmartSuggestions(machineId: string) {
    const sugContainer = document.getElementById('chat-suggestions');
    if (!sugContainer) return;
    const generic = [
        {q: 'Care sunt intervalele de mentenanță critice?', label: 'Intervale critice'},
        {q: 'Ce coduri de eroare sunt frecvente?', label: 'Coduri eroare'},
        {q: 'Câte piese de schimb avem per mașină?', label: 'Piese stoc'},
    ];
    const machineSpecific = machineId ? [
        {q: `Care este consumul actual al ${machineId}?`, label: `Consum ${machineId}`},
        {q: `Ce proceduri de mentenanță are ${machineId}?`, label: `Proceduri ${machineId}`},
        {q: `Ce alarme sau erori frecvente are ${machineId}?`, label: `Erori ${machineId}`},
        {q: `Care sunt piesele de schimb pentru ${machineId}?`, label: `Piese ${machineId}`},
        {q: `Arată statusul complet al ${machineId}`, label: `Status ${machineId}`},
        {q: `Ce intervenții recente s-au făcut la ${machineId}?`, label: `Istoric ${machineId}`},
    ] : generic;

    const all = machineId ? machineSpecific : [...generic,
        {q: 'Ce documente tehnice avem procesate per mașină?', label: 'Documente'},
        {q: 'Care sunt mașinile cu status defect?', label: 'Mașini defecte'},
    ];
    sugContainer.innerHTML = all.map(s =>
        `<button class="suggestion-chip" data-q="${escHtml(s.q)}">${escHtml(s.label)}</button>`
    ).join('');
    sugContainer.querySelectorAll('.suggestion-chip').forEach(chip => {
        chip.addEventListener('click', () => {
            (document.getElementById('chat-input') as HTMLInputElement).value = (chip as HTMLElement).dataset.q as string;
            sendChatMessage();
        });
    });
}

document.getElementById('chat-new-btn')?.addEventListener('click', () => {
    _chatHistory.length = 0;
    try {
        delete (window as unknown as { __chatLineId?: string }).__chatLineId;
    } catch (_) { /* ignore */ }
    const machineId = (document.getElementById('chat-machine') as HTMLInputElement)?.value || '';
    const contextNote = machineId
        ? `<p style="font-size:.85rem;color:var(--cyan)">📡 Context mașină: ${escHtml(machineId)} — date senzori, alarme și istoric.</p>`
        : '<p style="font-size:.85rem;color:var(--text-muted)">Selectează o mașină pentru context automat.</p>';
    const msgs = document.getElementById('chat-messages');
    if (msgs) {
        msgs.innerHTML = `<div class="chat-msg bot">
            <div class="chat-msg-avatar">🤖</div>
            <div class="chat-msg-content">
                <p>Conversație nouă. Cu ce te pot ajuta?</p>
                ${contextNote}
            </div>
        </div>`;
    }
    const suggestions = document.getElementById('chat-suggestions');
    if (suggestions) suggestions.style.display = '';
    _updateSmartSuggestions(machineId);
    document.getElementById('chat-input')?.focus();
});

/* ── Global AI Feedback (reusable across all AI interactions) ── */
export function _createFeedbackHtml(question: string|null|undefined, answer: string, aiSource: string|null|undefined, sources: ChatSource[] | null) {
    const fbId = 'fb-' + Date.now() + '-' + Math.random().toString(36).substr(2,5);
    return `<div class="chat-feedback" id="${fbId}" data-q="${escHtml(question)}" data-a="${escHtml(answer.substring(0,2000))}" data-src="${escHtml(aiSource)}" data-sources='${escHtml(JSON.stringify(sources||[]))}'>
        <button class="fb-btn fb-good" onclick="_globalFbVote('${fbId}','good')">👍</button>
        <button class="fb-btn fb-bad" onclick="_globalFbVote('${fbId}','bad')">👎</button>
        <button class="fb-btn fb-correct" onclick="_globalFbCorrect('${fbId}')">✏️ Corectează</button>
    </div>`;
}

export function _globalFbVote(fbId: string, vote: string) {
    const el = document.getElementById(fbId);
    if (!el) return;
    const q = el.dataset.q, a = el.dataset.a, src = el.dataset.src;
    let sources = [];
    try { sources = JSON.parse(el.dataset.sources as string); } catch(e){}
    if (vote === 'good') {
        _sendFeedback(vote, q, a, src, sources, '');
        el.innerHTML = '<span style="font-size:.8rem;color:var(--green)">👍 Mulțumim!</span>';
        return;
    }
    // Bad vote: REQUIRE correction so AI can actually learn.
    _showCorrectionForm(fbId, q, a, src, sources);
}

export function _globalFbCorrect(fbId: string) {
    const el = document.getElementById(fbId);
    if (!el) return;
    const q = el.dataset.q, a = el.dataset.a, src = el.dataset.src;
    let sources = [];
    try { sources = JSON.parse(el.dataset.sources as string); } catch(e){}
    _showCorrectionForm(fbId, q, a, src, sources);
}

/* ── Chat History ── */
export let _chPage = 1;

/* Insigna spunea „🔵 Cloud" pentru orice sursă diferită de `local` — adică și
   pentru cele 1150 de răspunsuri `db_fact` (citite direct din baza noastră), și
   pentru `colleague_brief`, și pentru `local_agentic`. Alături stă insigna de
   buget cloud, iar după istoric se judecă cât s-a cheltuit pe modele plătite:
   o insignă care minte aici umflă o factură care nu există. „Cloud" rămâne doar
   pentru ce chiar a plecat în afară. */
function _insignaSursa(sursa: string | null | undefined): string {
    const s = (sursa || '').toLowerCase().trim();
    if (s.startsWith('cloud')) {
        const vedere = s.includes('vision') ? ' (imagine)' : '';
        return `<span class="ch-badge ch-cloud">🔵 Cloud${vedere}</span>`;
    }
    if (s.startsWith('local')) return '<span class="ch-badge ch-local">🟢 Local</span>';
    if (s === 'db_fact') return '<span class="ch-badge ch-db">📚 Din baza noastră</span>';
    if (s.startsWith('colleague')) return '<span class="ch-badge ch-db">🤝 Coleg</span>';
    if (!s || s === 'none') return '<span class="ch-badge ch-other">— fără sursă</span>';
    return `<span class="ch-badge ch-other">${escHtml(sursa || '')}</span>`;
}

export async function _loadChatHistory(page?: number) {
    if (page) _chPage = page;
    const panel = document.getElementById('ch-results');
    if (!panel) return;

    const source = (document.getElementById('ch-filter-source') as HTMLInputElement)?.value || '';
    const reviewed = (document.getElementById('ch-filter-reviewed') as HTMLInputElement)?.value || '';
    const q = ((document.getElementById('ch-search') as HTMLInputElement)?.value || '').trim();

    panel.innerHTML = '<div style="text-align:center;padding:20px"><div class="typing-dots"><span></span><span></span><span></span></div></div>';

    const params = new URLSearchParams({ page: String(_chPage), per_page: '20' });
    if (source) params.set('source', source);
    if (reviewed) params.set('reviewed', reviewed);
    if (q) params.set('q', q);

    try {
        const result = await api(`/api/chat_history?${params}`);
        if (!result.data || result.data.length === 0) {
            panel.innerHTML = '<p style="text-align:center;color:var(--text-muted);padding:20px">Nicio conversație găsită.</p>';
            return;
        }

        // audit UX 31 iul 2026: și inginerul marchează feedback pe conversații
        const isMaster = state.currentUser.role === 'master' || state.currentUser.role === 'engineer';
        let html = `<p style="font-size:.82rem;color:var(--text-muted);margin-bottom:8px">${result.total} conversații — pagina ${result.page}/${Math.ceil(result.total/result.per_page)||1}</p>`;

        result.data.forEach((ch: RandChatHistory) => {
            const srcBadge = _insignaSursa(ch.ai_source);
            const reviewBadge = ch.reviewed_by
                ? `<span class="ch-badge ch-reviewed">✅ Revizuit</span>`
                : `<span class="ch-badge ch-unreviewed">⏳ Nerevizuit</span>`;
            const voteBadge = ch.feedback_vote === 'good' ? ' 👍' : ch.feedback_vote === 'bad' ? ' 👎' : ch.feedback_vote === 'correction' ? ' ✏️' : '';

            const cardId = `ch-card-${ch.id}`;
            html += `<div class="ch-card ${ch.reviewed_by ? 'ch-done' : ''}" id="${cardId}">
                <div class="ch-card-header" onclick="document.getElementById('${cardId}').classList.toggle('expanded')">
                    <div class="ch-card-meta">${srcBadge} ${reviewBadge}${voteBadge} <span class="ch-date">${escHtml((ch.created_at||'').substring(0,16))}</span></div>
                    <div class="ch-card-question">${escHtml((ch.question||'').substring(0,120))}</div>
                    <span class="scheme-expand-icon">▸</span>
                </div>
                <div class="ch-card-body">
                    <div class="ch-q-full"><strong>Întrebare:</strong> ${escHtml(ch.question)}</div>
                    <div class="ch-a-full"><strong>Răspuns AI:</strong><br>${escHtml((ch.answer||'').substring(0,1500)).replace(/\\n/g,'<br>')}</div>`;

            if (ch.feedback_correction) {
                html += `<div class="ch-correction-existing"><strong>✏️ Corecție anterioară:</strong> ${escHtml(ch.feedback_correction)}</div>`;
            }

            if (isMaster && !ch.reviewed_by) {
                html += `<div class="ch-review-actions" id="ch-review-${ch.id}">
                    <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:6px">
                        <button class="btn btn-sm" style="background:var(--green);color:#fff" onclick="_chReview(${ch.id},'good')">👍 Corect</button>
                        <button class="btn btn-sm" style="background:var(--red);color:#fff" onclick="_chReview(${ch.id},'bad')">👎 Incorect</button>
                    </div>
                    <textarea class="ch-correct-input" id="ch-correct-${ch.id}" rows="2" placeholder="Opțional: scrieți răspunsul corect aici..."></textarea>
                    <button class="btn btn-primary btn-sm" style="margin-top:4px" onclick="_chReviewWithCorrection(${ch.id})">✏️ Trimite corecția</button>
                </div>`;
            } else if (ch.reviewed_by) {
                html += `<div class="ch-reviewed-info">✅ Revizuit${ch.feedback_vote ? ' — ' + ch.feedback_vote : ''} de ${escHtml(ch.reviewed_by)} la ${escHtml((ch.reviewed_at||'').substring(0,16))}</div>`;
            }

            html += '</div></div>';
        });

        panel.innerHTML = html;
        renderPagination('#ch-pagination', result.total, result.per_page, _chPage, (pg: number|undefined) => _loadChatHistory(pg));
    } catch (err) {
        panel.innerHTML = `<p style="color:var(--red);padding:20px">Eroare: ${escHtml((err as Error).message)}</p>`;
    }
}

export async function _chReview(chatId: number, vote: string) {
    try {
        await fetch(`/api/chat_history/${chatId}/review`, {
            method: 'POST', headers: {'Content-Type':'application/json'},
            body: JSON.stringify({ vote })
        });
        const el = document.getElementById(`ch-review-${chatId}`);
        if (el) el.innerHTML = `<span style="color:var(--green);font-size:.85rem">✅ Marcat ca ${vote === 'good' ? 'corect' : 'incorect'}</span>`;
    } catch (err) { toast('Eroare', 'error'); }
}

export async function _chReviewWithCorrection(chatId: number) {
    const textarea = document.getElementById(`ch-correct-${chatId}`);
    const correction = textarea ? (textarea as HTMLInputElement).value.trim() : '';
    try {
        await fetch(`/api/chat_history/${chatId}/review`, {
            method: 'POST', headers: {'Content-Type':'application/json'},
            body: JSON.stringify({ vote: 'correction', correction })
        });
        const el = document.getElementById(`ch-review-${chatId}`);
        if (el) el.innerHTML = `<span style="color:var(--green);font-size:.85rem">✅ Corecție trimisă — AI va învăța din ea</span>`;
    } catch (err) { toast('Eroare', 'error'); }
}

export function _showCorrectionForm(fbId: string, question: string|undefined, answer: string|undefined, aiSource: string|undefined, srcData: ChatSource[]) {
    const fbDiv = document.getElementById(fbId);
    if (!fbDiv) return;
    fbDiv.innerHTML = `
        <div class="fb-correction-form">
            <p style="font-size:.85rem;margin:0 0 6px;color:var(--text)"><strong>✏️ Corectează acest răspuns:</strong></p>
            <textarea class="fb-correction-text" rows="3" placeholder="Scrieți răspunsul corect sau observația..."></textarea>
            <div class="fb-sources-check"></div>
            <div style="display:flex;gap:8px;margin-top:8px">
                <button class="btn btn-primary btn-sm fb-submit-btn">Trimite corecția</button>
                <button class="btn btn-outline btn-sm fb-cancel-btn">Anulează</button>
            </div>
        </div>
    `;

    // Source checkboxes for flagging
    const srcCheck = fbDiv.querySelector('.fb-sources-check');
    if (srcData.length > 0 && srcCheck) {
        srcCheck.innerHTML = `<div class="fb-flag-warning">
            <span class="fb-flag-icon">⚠️</span>
            <span>Bifați <strong>DOAR sursele GREȘITE</strong> — cele care au furnizat informații incorecte sau irelevante. Sursele bifate vor fi penalizate în viitoarele răspunsuri AI.</span>
        </div>`;
        srcData.forEach((s, i) => {
            const typeLabel = {document_extracts:'Documentație', procedure_steps:'Procedură', parts:'Piesă', maintenance_intervals:'Interval', maintenance_log:'Jurnal', cloud_learned:'Învățat AI'}[s.source as string] || s.source as string;
            // `value` rămâne indexul (așa îl citește trimiterea); `data-source-id`
            // spune CARE sursă e, ca să se poată verifica penalizarea pe server.
            srcCheck.innerHTML += `<label class="fb-src-label"><input type="checkbox" value="${i}" data-source-id="${escHtml(String(s.source_id ?? ''))}" data-source="${escHtml(String(s.source ?? ''))}"> <span class="fb-src-type">${escHtml(typeLabel)}</span> <span>${escHtml((s.title || '').substring(0, 80))}</span></label>`;
        });
    }

    fbDiv.querySelector('.fb-cancel-btn')!.addEventListener('click', () => {
        fbDiv.innerHTML = '<span style="font-size:.8rem;color:var(--text-muted)">Anulat</span>';
    });

    fbDiv.querySelector('.fb-submit-btn')!.addEventListener('click', () => {
        const ta = fbDiv.querySelector('.fb-correction-text');
        const correctionText = ta ? (ta as HTMLInputElement).value.trim() : '';
        // Mandatory correction: AI needs the right answer to learn.
        if (correctionText.length < 5) {
            if (ta) {
                (ta as HTMLElement).style.borderColor = 'var(--red)';
                (ta as HTMLInputElement).placeholder = 'OBLIGATORIU: scrieți răspunsul corect (min. 5 caractere) ca AI-ul să învețe.';
                (ta as HTMLElement).focus();
            }
            const warn = fbDiv.querySelector('.fb-correction-warn');
            if (!warn) {
                const w = document.createElement('div');
                w.className = 'fb-correction-warn';
                w.style.cssText = 'color:var(--red);font-size:.78rem;margin-top:4px';
                w.textContent = '⚠ Corecția este obligatorie pentru votul negativ — altfel AI-ul nu învață din greșeală.';
                fbDiv.querySelector('.fb-correction-form')!.appendChild(w);
            }
            return;
        }
        const flaggedSources: ChatSource[] = [];
        fbDiv.querySelectorAll('.fb-sources-check input:checked').forEach(cb => {
            const idx = parseInt((cb as HTMLInputElement).value);
            if (srcData[idx]) flaggedSources.push(srcData[idx]);
        });
        _sendFeedback('correction', question, answer, aiSource, srcData, correctionText, flaggedSources);
        fbDiv.innerHTML = '<span style="font-size:.8rem;color:var(--green)">✏️ Corecție trimisă și indexată în baza de cunoștințe! AI-ul va folosi această corecție la întrebări similare.</span>';
    });
}

export async function _sendFeedback(vote: string, question: string | undefined, answer: string | undefined, aiSource: string | undefined,
                                    sources: ChatSource[] | null, correction?: string, flaggedSources?: ChatSource[]) {
    try {
        const machineId = (document.getElementById('chat-machine') as HTMLInputElement)?.value || '';
        await fetch('/api/ai_feedback', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                vote, question, answer: answer!.substring(0, 2000),
                ai_source: aiSource, sources,
                correction: correction || '',
                flagged_sources: flaggedSources || [],
                machine_id: machineId,
            })
        });
    } catch (err) {
        console.error('Feedback error:', err);
    }
}

export const _chatHistory: Array<{ role: string; text: string }> = [];

/* ═══ CLARIFICAREA CU BUTOANE (etapa 3D, 10 oct 2026) ═══════════════════════════════════════════════
 * Când Claude nu știe sigur la ce utilaj sau la ce piesă se referă omul, întreabă cu 2–4 variante. Le arătăm ca
 * butoane, fiecare cu miniatura paginii din manual — mecanicul recunoaște piesa după desen. Varianta aleasă pleacă
 * odată cu mesajul (`clarificare_aleasa`): din ea învață glosarul (etapa 3E). */
interface VariantaClarificare { text: string; utilaj?: string | null; document_id?: number; pagina?: number; miniatura?: string }
interface Clarificare { intrebare: string; variante: VariantaClarificare[] }
let _clarificareAleasa: { intrebare_initiala: string; intrebare: string; varianta: VariantaClarificare } | null = null;

function _adaugaButoaneClarificare(msgDiv: HTMLElement, clar: Clarificare, intrebareInitiala: string): void {
    if (!clar || !Array.isArray(clar.variante) || !clar.variante.length) return;
    const wrap = document.createElement('div');
    wrap.className = 'chat-clarificare';
    clar.variante.forEach((v, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'chat-clarificare-btn';
        const unde = [v.utilaj || '', v.pagina ? `pag. ${v.pagina}` : ''].filter(Boolean).join(' · ');
        b.innerHTML = (v.miniatura ? `<img src="${escHtml(v.miniatura)}" alt="Pagina din manual" loading="lazy">` : '')
            + `<span class="chat-clarificare-text"><b>${i + 1}.</b> ${escHtml(v.text)}`
            + (unde ? `<small>${escHtml(unde)}</small>` : '') + `</span>`;
        b.addEventListener('click', () => {
            wrap.querySelectorAll('button').forEach(x => { (x as HTMLButtonElement).disabled = true; });
            b.classList.add('ales');
            _clarificareAleasa = { intrebare_initiala: intrebareInitiala, intrebare: clar.intrebare, varianta: v };
            const input = document.getElementById('chat-input') as HTMLInputElement | null;
            if (input) input.value = `Mă refer la: ${v.text}${v.utilaj ? ` (${v.utilaj})` : ''}`;
            void sendChatMessage();
        });
        wrap.appendChild(b);
    });
    (msgDiv.querySelector('.chat-msg-content') || msgDiv).appendChild(wrap);
}

export async function sendChatMessage(escalateMsg: string | null = null) {
    const input = $('#chat-input') as HTMLInputElement;
    const msg = escalateMsg || input.value.trim();
    if (!msg) return;

    if (!escalateMsg) input.value = '';
    if (!escalateMsg) {
        addChatMsg('user', msg);
        _chatHistory.push({role: 'user', text: msg});
    }
    const typingEl = addChatMsg('bot', escalateMsg ? 'Se solicită detalii de la AI Cloud...' : '', true);

    const machineSelect = document.getElementById('chat-machine') as HTMLSelectElement | null;
    let machineId = machineSelect?.value || '';
    const lineId = _getChatLineId();

    try {
        const forceCloud = _aiMode === 'cloud' || !!escalateMsg;
        // 4 oct 2026: butonul „Solicită detalii (AI Cloud)” din modul Local trimitea escalate + force_local,
        // iar serverul alegea tot localul — cererea explicită de Claude nu ajungea niciodată la Claude
        const forceLocal = _aiMode === 'local' && !escalateMsg;
        const agenticCb = document.getElementById('chat-agentic-cb') as HTMLInputElement | null;
        const useAgentic = !!(agenticCb?.checked) && !forceCloud;
        // AUTO folosește /api/chat (fără stream): acolo serverul poate trece la Cloud dacă încrederea locală e mică.
        // Doar „Doar AI Local” folosește stream (viteză percepută); mod Cloud = tot /api/chat.
        // Mod AGENT (orchestrator ReAct + mistral-small:24b) ocolește stream-ul (tool calls nu se streamează).
        const useStream = _aiMode === 'local' && !forceCloud && !useAgentic;

        if (useStream) {
            const streamDiv = addChatMsg('bot', '', false, false, null, 'local');
            const contentEl = streamDiv.querySelector('.chat-msg-content');
            let fullText = '';
            let streamSources = null, streamConfidence = 0, streamMedia = null;
            let streamAiSource: string | null = 'local';

            try {
                const res = await fetch('/api/chat/stream', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        message: msg,
                        machine_id: machineId,
                        line_id: lineId,
                        history: _chatHistory.slice(-8),
                        context_module: 'general',
                    }),
                });
                typingEl.remove();

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    throw new Error(errData.error || 'Stream failed');
                }

                const reader = res.body!.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const {done, value} = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, {stream: true});
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        try {
                            const chunk = JSON.parse(line.slice(6));
                            if (chunk.detected_machine && !machineId) {
                                const sel = document.getElementById('chat-machine') as HTMLSelectElement | null;
                                if (sel) { sel.value = chunk.detected_machine; sel.dispatchEvent(new Event('change')); }
                                machineId = chunk.detected_machine;
                            }
                            if (chunk.token) {
                                fullText += chunk.token;
                                const ctxBits = [lineId ? `Linia ${lineId}` : '', machineId || ''].filter(Boolean);
                                const machineTag = ctxBits.length
                                    ? `<span class="chat-context-tag" style="font-size:.75rem;color:var(--text-muted);margin-left:8px">📡 ${escHtml(ctxBits.join(' · '))}</span>`
                                    : '';
                                if (contentEl) {
                                    contentEl.innerHTML = `<span class="chat-source-tag local">🟢 AI Local</span>${machineTag}` + renderMarkdown(fullText);
                                    contentEl.closest('.chat-messages')?.scrollTo(0, contentEl.closest('.chat-messages')?.scrollHeight || 0);
                                }
                            }
                            if (chunk.done) {
                                streamSources = chunk.sources;
                                streamConfidence = chunk.confidence || 0;
                                if (chunk.source) streamAiSource = chunk.source;
                                // Textul corectat de verificarea de siguranță (19 aug 2026):
                                // tokenii deja afișați pot conține instrucțiuni periculoase pe
                                // care corecția le răstoarnă (ex. „opriți mașina" acolo unde
                                // oprirea pierde ireversibil datele encoderului). Îl înlocuim.
                                if (chunk.answer_final) {
                                    fullText = chunk.answer_final;
                                    if (contentEl) contentEl.innerHTML = `<span class="chat-source-tag local">🟢 AI Local</span>` + renderMarkdown(fullText);
                                }
                            }
                            if (chunk.media) streamMedia = chunk.media;
                            if (chunk.error) throw new Error(chunk.error);
                        } catch(pe) { if ((pe as Error).message !== 'Stream failed') console.warn(pe); }
                    }
                }

                _chatHistory.push({role: 'bot', text: fullText.substring(0, 500)});
                streamDiv.remove();
                addChatMsg('bot', fullText, false, false, null, streamAiSource || 'local', streamSources, streamConfidence, msg, streamMedia);
            } catch (streamErr) {
                typingEl?.remove?.();
                console.error('[Chat] Stream error:', streamErr);
                if (!fullText) {
                    streamDiv?.remove?.();
                    addChatMsg('bot', 'Stream error: ' + (streamErr instanceof Error ? streamErr.message : String(streamErr)), false, true);
                }
            }
        } else {
            const aleasa = _clarificareAleasa;
            _clarificareAleasa = null;
            const res = await fetch('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: msg, escalate: forceCloud, force_local: forceLocal,
                    agentic: useAgentic,
                    machine_id: machineId,
                    line_id: lineId,
                    history: _chatHistory.slice(-8),
                    context_module: 'general',
                    clarificare_aleasa: aleasa,
                })
            });
            const rawBody = await res.text();
            let data: RaspunsChat = {};
            try {
                data = rawBody ? JSON.parse(rawBody) : {};
            } catch {
                typingEl.remove();
                addChatMsg('bot',
                    'Serverul a returnat un răspuns neașteptat (de obicei pagină HTML la eroare gateway sau timeout). '
                    + 'Reîncearcă; dacă folosești AI Cloud, verifică serviciul și cheia API.',
                    false, true);
                return;
            }
            typingEl.remove();

            if (data.cloud_budget) updateCloudBudgetBadge(data.cloud_budget);

            if (data.error) {
                addChatMsg('bot', data.error, false, true);
            } else {
                if (data.detected_machine && !machineId) {
                    const sel = document.getElementById('chat-machine') as HTMLSelectElement | null;
                    if (sel) { sel.value = data.detected_machine; sel.dispatchEvent(new Event('change')); }
                }
                _chatHistory.push({role: 'bot', text: (data.response || '').substring(0, 500)});
                const divRaspuns = addChatMsg('bot', data.response as string, false, false, data.query_results, data.source, data.sources, data.confidence, msg, data.media);
                if (data.clarificare) _adaugaButoaneClarificare(divRaspuns, data.clarificare, aleasa ? aleasa.intrebare_initiala : msg);
            }
        }
    } catch (err) {
        typingEl?.remove?.();
        const errDetail = err instanceof Error ? err.message : String(err);
        console.error('[Chat] Connection error:', err);
        addChatMsg('bot', `Eroare de conexiune: ${errDetail}`, false, true);
    }
}

export function addChatMsg(role: string, text: string, typing = false, isError = false, queryResults: ChatQueryResult[] | null = null,
                    source: string | null = null, sources: ChatSource[] | null = null, confidence: number | null = null,
                    originalQuestion: string | null = null, media: ChatMedia | null = null): HTMLDivElement {
    const container = $('#chat-messages');
    const div = document.createElement('div');
    div.className = `chat-msg ${role}${typing ? ' typing' : ''}`;

    const avatar = role === 'bot' ? '🤖' : '👤';
    let sourceTag = '';
    if (role === 'bot' && source && !typing) {
        if (source === 'local') {
            const confPct = confidence ? Math.round(confidence * 100) : 0;
            sourceTag = `<span class="chat-source-tag local" title="Răspuns din AI local (Ollama + RAG)">🟢 AI Local (${confPct}%)</span>`;
        } else if (source === 'local_agentic') {
            const confPct = confidence ? Math.round(confidence * 100) : 0;
            sourceTag = `<span class="chat-source-tag local" style="background:#7c3aed;color:#fff" title="Răspuns AI Agent (mistral-small:24b cu apel tool-uri)">🧠 Agent (${confPct}%)</span>`;
        } else if (source === 'local_ft') {
            const confPct = confidence ? Math.round(confidence * 100) : 0;
            sourceTag = `<span class="chat-source-tag local" title="Răspuns din modelul fine-tuned fabrică">🟢 AI Fabrică (${confPct}%)</span>`;
        } else if (source === 'cloud') {
            sourceTag = `<span class="chat-source-tag cloud" title="Răspuns din AI Cloud">🔵 AI Cloud</span>`;
        } else if (source === 'db_fact') {
            sourceTag = `<span class="chat-source-tag" style="background:var(--border);color:var(--text)" title="Răspuns direct din baza de date (fără model AI)">📋 Date fabrică (DB)</span>`;
        }
    }

    div.innerHTML = `
        <div class="chat-msg-avatar">${avatar}</div>
        <div class="chat-msg-content">
            ${sourceTag}
            ${typing ? '<div class="typing-dots"><span></span><span></span><span></span></div>' : (role === 'user' ? escHtml(text) : renderMarkdown(text))}${isError ? ' ⚠️' : ''}
        </div>
    `;

    if (role === 'bot' && !typing && !isError) {
        const content = div.querySelector('.chat-msg-content');

        if (sources && sources.length > 0) {
            const noAnswer = /nu am (suficiente )?(informa|date|detalii)|nu (pot|știu) (să )?(răspund|oferi)|informații insuficiente/i.test(text);
            const imageSources = noAnswer ? [] : sources.filter((s) => s.image_url && (s.relevance || 0) >= 0.6);
            if (imageSources.length > 0) {
                const imgDiv = document.createElement('div');
                imgDiv.className = 'chat-source-images';
                imgDiv.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;margin-bottom:6px';
                imageSources.forEach((s) => {
                    imgDiv.innerHTML += `<div style="text-align:center">
                        <img src="${escHtml(s.image_url)}" alt="${escHtml(s.title||'')}"
                             style="max-width:200px;max-height:150px;border-radius:6px;cursor:pointer;border:1px solid var(--border)"
                             onclick="_openLightbox('${escHtml(s.image_url)}')">
                        <div style="font-size:.75rem;color:var(--text-muted);max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml((s.title||'').substring(0,40))}</div>
                    </div>`;
                });
                content!.appendChild(imgDiv);
            }

            const textSources = noAnswer ? [] : sources.filter((s) => !s.image_url || (s.relevance || 0) >= 0.55);
            if (textSources.length > 0) {
                const srcDiv = document.createElement('div');
                srcDiv.className = 'chat-sources';
                srcDiv.innerHTML = '<span class="chat-sources-label">📎 Surse:</span> ' +
                    textSources.map((s) => {
                        const icon = s.type === 'image_analysis' ? '🖼️' : s.type === 'procedure' ? '📋' : s.type === 'interval' ? '🔧' : s.type === 'part' ? '⚙️' : s.type === 'raw_page' ? '📄' : s.type === 'log' ? '📝' : '📄';
                        const extraData = s.page_number != null ? ` data-page="${s.page_number + 1}"` : '';
                        return `<button class="chat-source-btn" data-type="${s.source}" data-id="${s.source_id}" data-img="${escHtml(s.image_url||'')}"${extraData} title="${escHtml(s.title || '')}">${icon} ${escHtml((s.title || '').substring(0, 50))}</button>`;
                    }).join('');
                content!.appendChild(srcDiv);
                _attachSourceClickHandlers(srcDiv);
            }
        }

        if (media) {
            if (media.images && media.images.length > 0) {
                const mediaImgDiv = document.createElement('div');
                mediaImgDiv.className = 'chat-media-images';
                mediaImgDiv.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;padding:8px;background:var(--card-bg);border-radius:8px;border:1px solid var(--border)';
                let imgHtml = '<div style="width:100%;font-size:.75rem;color:var(--text-muted);margin-bottom:4px">📷 Imagini relevante din documentație:</div>';
                media.images.forEach((img) => {
                    imgHtml += `<div style="text-align:center">
                        <img src="${escHtml(img.url)}" alt="${escHtml(img.title||'')}"
                             style="max-width:200px;max-height:150px;border-radius:6px;cursor:pointer;border:1px solid var(--border)"
                             onclick="_openLightbox('${escHtml(img.url)}')">
                        <div style="font-size:.7rem;color:var(--text-muted);max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml((img.title||'').substring(0,40))}</div>
                    </div>`;
                });
                mediaImgDiv.innerHTML = imgHtml;
                content!.appendChild(mediaImgDiv);
            }
            if (media.pdf_pages && media.pdf_pages.length > 0) {
                const pdfDiv = document.createElement('div');
                pdfDiv.className = 'chat-media-pdf';
                pdfDiv.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;margin-top:8px';
                let pdfHtml = '<div style="width:100%;font-size:.75rem;color:var(--text-muted);margin-bottom:2px">📄 Pagini PDF relevante:</div>';
                media.pdf_pages.forEach((p) => {
                    pdfHtml += `<button class="btn btn-outline btn-sm" style="font-size:.8rem" onclick="window.open('/api/documents/${p.document_id}/view?page=${p.page}','_blank')">📄 ${escHtml((p.filename||'').substring(0,25))} — p.${p.page}</button>`;
                });
                pdfDiv.innerHTML = pdfHtml;
                content!.appendChild(pdfDiv);
            }
        }

        if (source === 'local' && originalQuestion) {
            const escBtn = document.createElement('button');
            escBtn.className = 'chat-escalate-btn';
            escBtn.innerHTML = '🔵 Solicită detalii suplimentare (AI Cloud)';
            escBtn.addEventListener('click', () => {
                escBtn.disabled = true;
                escBtn.textContent = 'Se procesează...';
                sendChatMessage(originalQuestion);
            });
            content!.appendChild(escBtn);
        }

        // Feedback panel (for all bot responses with content)
        if (text && !isError && !typing) {
            const fbId = 'fb-' + Date.now() + '-' + Math.random().toString(36).substr(2,5);
            const feedbackDiv = document.createElement('div');
            feedbackDiv.className = 'chat-feedback';
            feedbackDiv.id = fbId;
            feedbackDiv.innerHTML = `
                <button class="fb-btn fb-good" data-vote="good" title="Răspuns bun">👍</button>
                <button class="fb-btn fb-bad" data-vote="bad" title="Răspuns slab/incorect">👎</button>
                <button class="fb-btn fb-correct" data-vote="correct" title="Corectează răspunsul">✏️ Corectează</button>
            `;
            content!.appendChild(feedbackDiv);

            const srcData: ChatSource[] = (sources || []).map((s) => ({source: s.source, source_id: s.source_id, title: s.title}));
            feedbackDiv.querySelectorAll('.fb-btn').forEach(btn => {
                btn.addEventListener('click', () => {
                    const vote = (btn as HTMLElement).dataset.vote;
                    /* 👎 cere corecția, ca în Diagnostic și la Identificare piesă.
                       Aici trimitea direct un vot negativ și atât — același gest
                       avea două înțelesuri în două ecrane, iar din „e slab" fără
                       explicație modelul nu învață nimic. Un singur contract. */
                    if (vote === 'correct' || vote === 'bad') {
                        _showCorrectionForm(fbId, originalQuestion || '', text, source || 'unknown', srcData);
                    } else {
                        _sendFeedback(vote as string, originalQuestion || '', text, source || 'unknown', srcData, '');   // butoanele au mereu data-vote
                        feedbackDiv.innerHTML =
                            '<span style="font-size:.8rem;color:var(--green)">👍 Mulțumim! Răspunsul a fost marcat ca util.</span>';
                    }
                });
            });
        }

        if (queryResults && queryResults.length > 0) {
            queryResults.forEach((qr) => {
                if (qr.columns && qr.rows && qr.rows.length > 0) {
                    const tableDiv = document.createElement('div');
                    tableDiv.className = 'chat-query-table';
                    let html = `<details><summary style="cursor:pointer;font-size:.75rem;color:var(--text-muted);margin:.3rem 0">📊 Date brute (${qr.rows.length} rânduri)</summary>`;
                    html += '<table><thead><tr>';
                    qr.columns.forEach((c: string|null|undefined) => { html += `<th>${escHtml(c)}</th>`; });
                    html += '</tr></thead><tbody>';
                    qr.rows.slice(0, 30).forEach((row) => {
                        html += '<tr>';
                        qr.columns!.forEach((c: string|number) => { html += `<td>${escHtml(String(row[c] ?? ''))}</td>`; });
                        html += '</tr>';
                    });
                    html += '</tbody></table>';
                    if (qr.rows.length > 30) html += `<p style="font-size:.72rem;color:var(--text-muted)">... și ${qr.rows.length - 30} rânduri adiționale</p>`;
                    html += '</details>';
                    tableDiv.innerHTML = html;
                    content!.appendChild(tableDiv);
                }
            });
        }
    }

    container!.appendChild(div);
    container!.scrollTop = container!.scrollHeight;
    return div;
}

export function renderMarkdown(text: string|null|undefined) {
    if (!text) return '';
    // 2026-07-26 (test adversarial): textul intra NEESCAPAT în innerHTML, iar
    // CSP-ul permite 'unsafe-inline' — orice HTML din răspunsul AI (care citează
    // text din manuale sau reformulează întrebarea operatorului) se executa în
    // browser. Escapăm ÎNTÂI, apoi aplicăm markdown-ul (tag-urile de mai jos
    // sunt generate de noi, deci rămân valide).
    let html = escHtml(text);
    html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_, lang, code) => {
        return `<pre><code>${code.trim()}</code></pre>`;
    });
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    // 13 sep 2026: citarea [Document X, pagina Y] deschide pagina din PDF —
    // ca în manual, textul și desenul stau la un clic distanță.
    html = html.replace(/\[Document\s+(\d+),\s*pagina\s+(\d+)\]/g,
        (_m, d, pg) => `<a href="/api/documents/${d}/view?page=${pg}" target="_blank" rel="noopener" class="chat-citare" title="Deschide pagina ${pg} din document">[Document ${d}, pagina ${pg}]</a>`);

    html = html.replace(/^\|(.+)\|$/gm, (match) => {
        return match;
    });

    const lines = html.split('\n');
    let result = '';
    let inList = false;
    let inTable = false;
    let tableRows = [];
    let numberedSteps: string[] = [];

    function flushNumberedSteps() {
        if (numberedSteps.length < 3) {
            result += '<ol>';
            numberedSteps.forEach(s => { result += `<li>${s}</li>`; });
            result += '</ol>';
        } else {
            const wfId = 'wf-' + Date.now() + '-' + Math.random().toString(36).substr(2,5);
            result += `<div class="ai-workflow" id="${wfId}">`;
            result += `<div class="wf-header"><span class="wf-title">Procedură pas-cu-pas</span><span class="wf-progress" id="${wfId}-prog">0/${numberedSteps.length}</span></div>`;
            numberedSteps.forEach((s, idx) => {
                /* `onchange="_wfUpdate(...)"` căuta funcția în `window`, dar ea e
                   locală modulului după împachetare — deci bifarea unui pas arunca
                   ReferenceError și progresul rămânea pe 0/N. Ținem datele pe
                   element și le prindem cu un ascultător delegat (vezi mai jos). */
                result += `<label class="wf-step" data-idx="${idx}"><input type="checkbox" class="wf-check" data-wf-id="${wfId}" data-wf-total="${numberedSteps.length}"><span class="wf-num">${idx+1}</span><span class="wf-text">${s}</span></label>`;
            });
            result += '</div>';
        }
        numberedSteps = [];
    }

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        if (line.startsWith('|') && line.endsWith('|')) {
            if (numberedSteps.length) flushNumberedSteps();
            if (!inTable) { inTable = true; tableRows = []; }
            if (!/^\|[\s\-:|]+\|$/.test(line)) {
                tableRows.push(line);
            }
            continue;
        } else if (inTable) {
            result += buildMarkdownTable(tableRows);
            inTable = false;
            tableRows = [];
        }

        if (line.startsWith('- ') || line.startsWith('* ')) {
            if (numberedSteps.length) flushNumberedSteps();
            if (!inList) { result += '<ul>'; inList = true; }
            result += `<li>${line.slice(2)}</li>`;
        } else if (/^\d+\.\s/.test(line)) {
            if (inList) { result += '</ul>'; inList = false; }
            numberedSteps.push(line.replace(/^\d+\.\s/, ''));
        } else {
            if (numberedSteps.length) flushNumberedSteps();
            if (inList) { result += '</ul>'; inList = false; }
            if (line.startsWith('### ')) {
                result += `<strong>${line.slice(4)}</strong><br>`;
            } else if (line.startsWith('## ')) {
                result += `<strong>${line.slice(3)}</strong><br>`;
            } else if (line.startsWith('# ')) {
                result += `<strong>${line.slice(2)}</strong><br>`;
            } else if (line === '') {
                result += '<br>';
            } else {
                result += `<p>${line}</p>`;
            }
        }
    }
    if (numberedSteps.length) flushNumberedSteps();
    if (inList) result += '</ul>';
    if (inTable) result += buildMarkdownTable(tableRows);

    result = result.replace(
        /\[Document\s+(\d+),\s*(?:pagina|p\.?)\s*(\d+)\]/g,
        '<a href="/api/documents/$1/view?page=$2" target="_blank" style="color:#00b4ff;text-decoration:underline;font-weight:600">📖 Document $1, p.$2</a>'
    );

    return result;
}

/* Un singur ascultător pe document, legat o dată: procedurile din chat se
   randează ca text HTML oricând sosește un răspuns, deci n-avem unde lega
   ascultători per element fără să-i legăm de fiecare dată. */
document.addEventListener('change', (ev) => {
    const bifa = (ev.target as HTMLElement)?.closest?.('.wf-check') as HTMLInputElement | null;
    if (!bifa || !bifa.dataset.wfId) return;
    _wfUpdate(bifa.dataset.wfId, parseInt(bifa.dataset.wfTotal || '0', 10));
});

function _wfUpdate(wfId: string, total: number) {
    const wf = document.getElementById(wfId);
    if (!wf) return;
    const checked = wf.querySelectorAll('.wf-check:checked').length;
    const prog = wf.querySelector(`#${wfId}-prog`);
    if (prog) prog.textContent = `${checked}/${total}`;
    wf.querySelectorAll('.wf-step').forEach(s => {
        s.classList.toggle('wf-done', (s.querySelector('.wf-check') as HTMLInputElement)?.checked);
    });
    if (checked === total) {
        if (!wf.querySelector('.wf-complete')) {
            const badge = document.createElement('div');
            badge.className = 'wf-complete';
            badge.textContent = 'Procedură finalizată!';
            wf.appendChild(badge);
        }
    } else {
        wf.querySelector('.wf-complete')?.remove();
    }
}

export function buildMarkdownTable(rows: string[]) {   // rândurile tabelului markdown, ca text
    if (rows.length === 0) return '';
    let html = '<table><thead><tr>';
    const headerCells = rows[0].split('|').filter((c: string) => c.trim());
    headerCells.forEach((c: string) => { html += `<th>${c.trim()}</th>`; });
    html += '</tr></thead><tbody>';
    for (let i = 1; i < rows.length; i++) {
        const cells = rows[i].split('|').filter((c: string) => c.trim());
        html += '<tr>';
        cells.forEach((c: string) => { html += `<td>${c.trim()}</td>`; });
        html += '</tr>';
    }
    html += '</tbody></table>';
    return html;
}
